
  import {computed, defineComponent, onMounted, reactive, toRefs, watch} from "vue";
  import {HeaderGetters} from "@/store/modules/header";
  import StatisticServiceV2 from "@/service/StatisticServiceV2";
  import moment, {Moment} from "moment";
  import {EmployeeSummariesLine, EmployeeSummariesMonth} from "@/model/EmployeeSummaries";
  import {WageType} from "@/enums";
  import {DataTableHeader} from "vuetify";
  import store from "@/store/store";

  export default defineComponent({
    name: "StatisticEmployeesV2",
    setup(props, context) {

      const initialState = {
        items: new Array<EmployeeSummariesLine>(),
        loading: false,
        twelveMonthsView: false
      };

      const headers = computed(() => {
        let result = new Array<DataTableHeader>();
        result.push(
          {
            text: "ID",
            align: "start",
            width: 80,
            value: "employee.employeeNumber"
          },
          {
            text: "Vorname",
            align: "start",
            width: 120,
            value: "employee.firstName"
          },
          {
            text: "Nachname",
            align: "start",
            width: 120,
            value: "employee.lastName"
          },
          {
            text: "Ø Stunden/Tag",
            align: "end",
            width: 120,
            sortable: false,
            value: ""
          },
          {
            text: "Ø Tagessatz",
            align: "end",
            width: 120,
            sortable: false,
            value: ""
          }
        );

        let numberOfMonths = state.twelveMonthsView ? 12 : 3;
        for (let i = 0; i < numberOfMonths; i++) {
          result.push(
            {
              text: "# Tage",
              align: "end",
              width: 80,
              sortable: false,
              value: ""
            },
            {
              text: "# Stunden",
              align: "end",
              width: 120,
              sortable: false,
              value: ""
            },
            {
              text: "Gehalt",
              align: "start",
              sortable: false,
              value: "wage"
            },
            {
              text: "Gehaltsart",
              align: "start",
              sortable: false,
              value: "wageType"
            }
          );
        }

        return result;
      });

      const state = reactive({...initialState});

      const selectedMonth = computed(() => store.getters["header/" + HeaderGetters.GET_MONTH]);

      const from = computed(() => {
        const monthsToSubstract = state.twelveMonthsView ? 11 : 2;
        return moment(selectedMonth.value).subtract(monthsToSubstract, "months");
      });

      const to = computed(() => {
        return moment(selectedMonth.value);
      });

      const months = computed(() => {
        let result = [];

        let numberOfMonths = state.twelveMonthsView ? 12 : 3;
        for (let i = 0; i < numberOfMonths; i++) {
          let month = to.value.clone();
          result.push(month.subtract(i, "months"));
        }

        return result;
      });

      const fetchEmployeeSummary = (fromDate: Moment, toDate: Moment) => {
        const fromString = fromDate.format("YYYY-MM");
        const toString = toDate.format("YYYY-MM");

        state.items = [];
        state.loading = true;
        StatisticServiceV2.getEmployeeSummaries(fromString, toString)
          .then(response => {
            state.items = response.data.employees
              // Only keep the months with an hourly wage; months without terms are dropped as well
              .map(item => ({...item, months: item.months.filter(month => month.terms?.wageType === WageType.WAG)}))
              // Remove those entries where no month entry has a wage entry, e.g. in case all entries are salaried
              .filter(item => item.months.length > 0);
          })
          .finally(() => state.loading = false);
      };

      watch(() => selectedMonth, () => {
        fetchEmployeeSummary(from.value, to.value);
      }, {deep: true});

      watch(() => state.twelveMonthsView, () => {
        fetchEmployeeSummary(from.value, to.value);
      });

      const sumMinutes = (month: EmployeeSummariesMonth) => month.time.workedMinutes + month.time.travelMinutes;

      const calculateAverageHoursPerDay = (item: EmployeeSummariesLine) => {
        const totalMinutes = item.months.reduce((currentValue, month) => sumMinutes(month) + currentValue, 0);
        const totalWorkingDays = item.months.reduce((currentValue, month) => month.time.workingDays + currentValue, 0);
        return totalWorkingDays > 0 ? totalMinutes / 60 / totalWorkingDays : 0;
      };

      const calculateAverageWage = (item: EmployeeSummariesLine) => {
        const wageSum = item.months.reduce((currentValue, month) => (month.terms?.wage ?? 0) + currentValue, 0);

        return wageSum / item.months.length;
      };

      const hasMonthData = (month: Moment, line: EmployeeSummariesLine) => {
        return line.months.some(item => item.month === month.format("YYYY-MM"));
      };

      const getMonthData = (month: Moment, line: EmployeeSummariesLine) => {
        return line.months.find(item => item.month === month.format("YYYY-MM"));
      };

      onMounted(() => {
        fetchEmployeeSummary(from.value, to.value);
      });

      return { ...toRefs(state), months, headers, from, to, hasMonthData, getMonthData, sumMinutes, calculateAverageHoursPerDay, calculateAverageWage };
    }
  });
